import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { postData1 } from "../../services";
import { Loader } from "../../components/Loader";

const emptyUpdate = {
  customer_id: "",
  product_id: "",
  assigned_team_ids: [],
  source: "",
  priceshare: false,
  priceshare_notes: "",
  pi: false,
  pi_notes: "",
  email: false,
  whatsapp: false,
};

const CustomerManagement = () => {
  const [section, setSection] = useState("updates");
  const [options, setOptions] = useState({
    teams: [],
    sources: [],
    updates: [],
  });
  const [teamName, setTeamName] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [users, setUsers] = useState([]);
  const [userPage, setUserPage] = useState(1);
  const [userPaging, setUserPaging] = useState({ totalPages: 1, total: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(emptyUpdate);
  const [editingUid, setEditingUid] = useState(null);
  const [viewModalUpdate, setViewModalUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const userRequest = useRef(0);
  const productRequest = useRef(0);
  const formRef = useRef(null);

  const loadOptions = useCallback(async () => {
    const response = await postData1(
      "admin/customer_update_options/",
      {},
      true,
      true
    );
    if (response?.status_code !== 200) {
      throw new Error(response?.msg || "Could not load customer update options.");
    }
    setOptions(response.data);
  }, []);

  const loadUsers = useCallback(async (query, page, append = false) => {
    const requestId = ++userRequest.current;
    const response = await postData1(
      "admin/customer_update_users/",
      { q: query, _page: page, _limit: 50 },
      true,
      true
    );
    if (response?.status_code !== 200) {
      throw new Error(response?.msg || "Could not load registered users.");
    }
    if (requestId !== userRequest.current) return;

    const results = response.data?.users || [];
    setUsers((current) => (append ? [...current, ...results] : results));
    setUserPage(response.data?.page || 1);
    setUserPaging({
      totalPages: response.data?.total_pages || 1,
      total: response.data?.total || 0,
    });
  }, []);

  const fetchProductSuggestions = useCallback(async (query) => {
    const requestId = ++productRequest.current;
    if (!query) {
      setProductSuggestions([]);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/product/improved-product/generic_search/`,
        {
          params: { search: query, isSearch: true, page_size: 8 },
          headers: {
            accept: "application/json",
            Authorization: `Token ${localStorage.getItem("adminAuth")}`,
          },
        }
      );
      if (requestId !== productRequest.current) return;

      const data = response?.data;
      if (Array.isArray(data?.results)) {
        setProductSuggestions(data.results);
      } else if (Array.isArray(data)) {
        setProductSuggestions(data);
      } else if (Array.isArray(data?.products)) {
        setProductSuggestions(data.products);
      } else {
        setProductSuggestions([]);
      }
    } catch (error) {
      if (requestId !== productRequest.current) return;
      console.error("Failed to fetch product suggestions:", error);
      setProductSuggestions([]);
      toast.error("Failed to fetch products.");
    }
  }, []);

  useEffect(() => {
    let active = true;
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        await loadOptions();
      } catch (error) {
        if (active) {
          console.error("Failed to load customer management data:", error);
          toast.error(error.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchInitialData();
    return () => {
      active = false;
    };
  }, [loadOptions]);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      try {
        await loadUsers(userQuery.trim(), 1);
      } catch (error) {
        if (active) {
          console.error("Failed to load registered users:", error);
          toast.error(error.message);
        }
      }
    }, 250);

    return () => {
      active = false;
      userRequest.current += 1;
      clearTimeout(timer);
    };
  }, [loadUsers, userQuery]);

  useEffect(() => {
    const selectedName =
      selectedProduct?.equip_name ||
      selectedProduct?.name ||
      selectedProduct?.title;
    if (selectedProduct && productQuery === selectedName) {
      productRequest.current += 1;
      setProductSuggestions([]);
      return undefined;
    }

    const query = productQuery.trim();
    if (!query) {
      productRequest.current += 1;
      setProductSuggestions([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      fetchProductSuggestions(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchProductSuggestions, productQuery, selectedProduct]);

  useEffect(() => {
    if (!formData.customer_id || !formData.product_id) return;
    const existing = options.updates.find(
      (update) =>
        String(update.customer_id) === String(formData.customer_id) &&
        String(update.product_id) === String(formData.product_id)
    );
    if (!existing) return;
    // Don't overwrite if the user is already editing this record
    if (editingUid && String(editingUid) === String(existing.uid)) return;

    const teamIds = Array.isArray(existing.assigned_team_ids) && existing.assigned_team_ids.length
      ? existing.assigned_team_ids
      : existing.assigned_team_id
      ? [existing.assigned_team_id]
      : [];

    setFormData((current) => ({
      ...current,
      assigned_team_ids: teamIds,
      source:
        selectedUser &&
        String(selectedUser.id) === String(formData.customer_id)
          ? selectedUser.source ?? selectedUser.source_name ?? ""
          : existing.source || existing.source_name || "",
      priceshare: Boolean(existing.priceshare),
      priceshare_notes: existing.priceshare_notes || "",
      pi: Boolean(existing.pi),
      pi_notes: existing.pi_notes || "",
      email: Boolean(existing.email),
      whatsapp: Boolean(existing.whatsapp),
    }));
    if (!editingUid) {
      setEditingUid(existing.uid);
    }
  }, [
    formData.customer_id,
    formData.product_id,
    options.updates,
    editingUid,
    selectedUser,
  ]);

  const resetForm = () => {
    setEditingUid(null);
    setFormData(emptyUpdate);
    setSelectedUser(null);
    setSelectedProduct(null);
    setProductQuery("");
    setUserQuery("");
    setShowProductDropdown(false);
  };

  const handleEditUpdate = (update) => {
    const teamIds = Array.isArray(update.assigned_team_ids) && update.assigned_team_ids.length
      ? update.assigned_team_ids
      : update.assigned_team_id
      ? [update.assigned_team_id]
      : [];

    setEditingUid(update.uid);
    setFormData({
      customer_id: String(update.customer_id || ""),
      product_id: String(update.product_id || ""),
      assigned_team_ids: teamIds,
      source: update.source || update.source_name || "",
      priceshare: Boolean(update.priceshare),
      priceshare_notes: update.priceshare_notes || "",
      pi: Boolean(update.pi),
      pi_notes: update.pi_notes || "",
      email: Boolean(update.email),
      whatsapp: Boolean(update.whatsapp),
    });

    const userObj = {
      id: update.customer_id,
      name: update.customer_name || "Customer",
      email: update.customer_email || "",
      mobile: update.customer_mobile || "",
    };
    setSelectedUser(userObj);

    const prodObj = {
      id: update.product_id,
      equip_name: update.product_name || "Product",
      name: update.product_name || "Product",
    };
    setSelectedProduct(prodObj);
    setProductQuery(update.product_name || "");
    setShowProductDropdown(false);

    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const createTeamMember = async (event) => {
    event.preventDefault();
    const name = teamName.trim();
    if (!name) {
      toast.error("Enter a team member name.");
      return;
    }

    setSaving(true);
    try {
      const response = await postData1(
        "admin/assign_team_members/",
        { name },
        true,
        true
      );
      if (response?.status_code !== 201) {
        toast.error(response?.msg || "Could not create team member.");
        return;
      }
      setTeamName("");
      await loadOptions();
      toast.success("Team member created.");
    } catch (error) {
      console.error("Failed to create team member:", error);
      toast.error(error.message || "Could not create team member.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCustomerChange = (event) => {
    const customerId = event.target.value;
    const customer =
      users.find((user) => String(user.id) === customerId) || null;
    setSelectedUser(customer);
    setFormData((current) => ({
      ...current,
      customer_id: customerId,
      source: customer?.source ?? customer?.source_name ?? "",
    }));
    setEditingUid(null);
  };

  const handleProductInput = (value) => {
    productRequest.current += 1;
    setProductQuery(value);
    setSelectedProduct(null);
    setProductSuggestions([]);
    setShowProductDropdown(true);
    setFormData((current) => ({ ...current, product_id: "" }));
    setEditingUid(null);
  };

  const handleSelectProduct = (product) => {
    const productId = product.id || product.product_id;
    if (!productId) {
      toast.error("This catalog result does not have a product ID.");
      return;
    }
    setSelectedProduct(product);
    setProductQuery(product.equip_name || product.name || product.title || "");
    setProductSuggestions([]);
    setShowProductDropdown(false);
    setFormData((current) => ({
      ...current,
      product_id: String(productId),
    }));
    setEditingUid(null);
  };

  const saveCustomerUpdate = async (event) => {
    event.preventDefault();
    if (!formData.customer_id || !formData.product_id) {
      toast.error("Select a registered user and product.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        assigned_team_ids: formData.assigned_team_ids || [],
        uid: editingUid || undefined,
      };
      const response = await postData1(
        "admin/save_customer_update/",
        payload,
        true,
        true
      );
      if (response?.status_code !== 200) {
        toast.error(response?.msg || "Could not save customer update.");
        return;
      }
      await loadOptions();
      toast.success(
        editingUid
          ? "Customer update details updated successfully."
          : "Customer update saved."
      );
      resetForm();
    } catch (error) {
      console.error("Failed to save customer update:", error);
      toast.error(error.message || "Could not save customer update.");
    } finally {
      setSaving(false);
    }
  };

  const loadMoreUsers = async () => {
    if (userPage >= userPaging.totalPages) return;
    try {
      await loadUsers(userQuery.trim(), userPage + 1, true);
    } catch (error) {
      console.error("Failed to load more registered users:", error);
      toast.error(error.message || "Could not load more users.");
    }
  };

  return (
    <section className="space-y-6 p-2 md:p-5">
      <header>
        <h1 className="text-2xl font-bold text-slate-800">CUSTOMER MANAGEMENT</h1>
        <p className="mt-1 text-sm text-slate-500">
          Assign team members and track customer activity, source, and pricing notes by product.
        </p>
      </header>

      <div className="flex gap-3 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setSection("updates")}
          className={`border-b-2 px-4 py-3 font-semibold transition-colors ${
            section === "updates"
              ? "border-teal-600 text-teal-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Customer Update
        </button>
        <button
          type="button"
          onClick={() => setSection("teams")}
          className={`border-b-2 px-4 py-3 font-semibold transition-colors ${
            section === "teams"
              ? "border-teal-600 text-teal-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Assign Team Members
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : section === "teams" ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(280px,1fr)_2fr]">
          <form
            onSubmit={createTeamMember}
            className="h-fit space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-bold text-slate-800">Create team member</h2>
            <label className="block text-sm font-medium text-slate-600" htmlFor="team-name">
              Name
            </label>
            <input
              id="team-name"
              value={teamName}
              onChange={(event) => setTeamName(event.target.value)}
              maxLength={100}
              required
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
              placeholder="Enter team member name"
            />
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-teal-700 px-5 py-2 font-semibold text-white hover:bg-teal-800 disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving..." : "Create"}
            </button>
          </form>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <h2 className="border-b border-slate-200 px-5 py-4 text-lg font-bold text-slate-800">
              Team members ({options.teams.length})
            </h2>
            {options.teams.length ? (
              <ul className="divide-y divide-slate-100">
                {options.teams.map((team) => (
                  <li className="px-5 py-3 text-slate-700" key={team.id}>
                    {team.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-5 text-slate-500">No team members have been added.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <form
            ref={formRef}
            onSubmit={saveCustomerUpdate}
            className="space-y-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  {editingUid ? "Edit Customer Update Details" : "Update customer status for a product"}
                </h2>
                <p className="text-xs text-slate-500">
                  {editingUid
                    ? "Update source, team assignment, communication status, and inquiry/share notes."
                    : "Select a customer and catalog product to record or update inquiry details."}
                </p>
              </div>
              {editingUid && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Clear / New Entry
                </button>
              )}
            </div>

            {editingUid && (
              <div className="flex items-center justify-between rounded-lg bg-teal-50 border border-teal-200 px-4 py-3 text-sm text-teal-900">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-teal-600 animate-pulse"></span>
                  <span>
                    Editing details for customer{" "}
                    <strong>{selectedUser?.name || "Customer"}</strong> and product{" "}
                    <strong>
                      {selectedProduct?.equip_name || selectedProduct?.name || "Product"}
                    </strong>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded border border-teal-300 bg-white px-2.5 py-1 text-xs font-medium text-teal-800 hover:bg-teal-100 transition-colors"
                >
                  Cancel Edit
                </button>
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-600" htmlFor="user-search">
                  Registered user
                </label>
                <input
                  id="user-search"
                  value={userQuery}
                  onChange={(event) => setUserQuery(event.target.value)}
                  className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
                  placeholder="Search by name, email, or mobile"
                />
                <select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleCustomerChange}
                  required
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
                >
                  <option value="">Select a user</option>
                  {selectedUser &&
                    !users.some(
                      (user) => String(user.id) === String(selectedUser.id)
                    ) && (
                      <option value={selectedUser.id}>
                        {selectedUser.name} — {selectedUser.email || selectedUser.mobile}
                      </option>
                    )}
                  {users.map((user) => (
                    console.log("Rendering user option:", user),
                    <option value={user.id} key={user.id}>
                      {user.name} — {user.email || user.mobile}
                    </option>
                  ))}
                </select>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>
                    Showing newest users first ({userPaging.total} registered)
                  </span>
                  {userPage < userPaging.totalPages && (
                    <button
                      type="button"
                      onClick={loadMoreUsers}
                      className="font-semibold text-teal-700 hover:underline"
                    >
                      Load more
                    </button>
                  )}
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-slate-600" htmlFor="customer-product">
                  Catalog product
                </label>
                <input
                  id="customer-product"
                  type="search"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={showProductDropdown && Boolean(productQuery)}
                  aria-controls="customer-product-suggestions"
                  value={productQuery}
                  onChange={(event) => handleProductInput(event.target.value)}
                  onFocus={() => {
                    if (!formData.product_id) setShowProductDropdown(true);
                  }}
                  autoComplete="off"
                  placeholder="Search active products by name or brand"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
                />
                {!formData.product_id && (
                  <span className="mt-1 block text-xs text-slate-500">
                    Choose a product from the live catalog search results.
                  </span>
                )}
                {showProductDropdown && productQuery.trim() && (
                  <div
                    id="customer-product-suggestions"
                    role="listbox"
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl"
                  >
                    {productSuggestions.length ? (
                      productSuggestions.slice(0, 8).map((product) => (
                        <button
                          key={product.id || product.product_id || product.uid}
                          type="button"
                          role="option"
                          aria-selected={false}
                          onClick={() => handleSelectProduct(product)}
                          className="w-full border-b border-slate-100 px-4 py-3 text-left text-sm text-slate-700 last:border-0 hover:bg-slate-50"
                        >
                          <span className="block font-semibold">
                            {product.equip_name ||
                              product.name ||
                              product.title ||
                              "Product"}
                          </span>
                          {product.brand && (
                            <span className="mt-1 block text-xs text-slate-500">
                              {product.brand}
                            </span>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        No products found.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600" htmlFor="assigned-teams">
                  Assigned team member(s)
                </label>
                <Select
                  id="assigned-teams"
                  isMulti
                  name="assigned_team_ids"
                  options={options.teams.map((team) => ({
                    value: team.id,
                    label: team.name,
                  }))}
                  value={options.teams
                    .filter((team) =>
                      formData.assigned_team_ids?.map(String).includes(String(team.id))
                    )
                    .map((team) => ({ value: team.id, label: team.name }))}
                  onChange={(selectedOptions) => {
                    setFormData((current) => ({
                      ...current,
                      assigned_team_ids: (selectedOptions || []).map((opt) => opt.value),
                    }));
                  }}
                  placeholder="Select one or more team members..."
                  className="mt-2 text-sm"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: state.isFocused ? "#0d9488" : "#cbd5e1",
                      borderRadius: "0.5rem",
                      minHeight: "42px",
                      boxShadow: state.isFocused ? "0 0 0 1px #0d9488" : "none",
                      "&:hover": {
                        borderColor: state.isFocused ? "#0d9488" : "#94a3b8",
                      },
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: "#ccfbf1",
                      borderRadius: "0.375rem",
                      border: "1px solid #99f6e4",
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: "#115e59",
                      fontWeight: "600",
                      fontSize: "0.8125rem",
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: "#0f766e",
                      "&:hover": {
                        backgroundColor: "#0d9488",
                        color: "#ffffff",
                      },
                    }),
                  }}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  Select one or more team members to assign to this customer.
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600" htmlFor="source-input">
                  Source
                </label>
                <input
                  id="source-input"
                  type="text"
                  name="source"
                  value={formData.source}
                  onChange={handleUpdateField}
                  placeholder="e.g. Google Ads, WhatsApp, Direct, Referral"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["priceshare", "Price shared"],
                ["pi", "Price inquiry"],
                ["email", "Email contacted"],
                ["whatsapp", "WhatsApp contacted"],
              ].map(([name, label]) => (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" key={name}>
                  <input
                    type="checkbox"
                    name={name}
                    checked={formData[name]}
                    onChange={handleUpdateField}
                    className="h-4 w-4 accent-teal-700 rounded"
                  />
                  {label}
                </label>
              ))}
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <label className="block text-sm font-medium text-slate-600">
                Price shared notes
                <textarea
                  name="priceshare_notes"
                  value={formData.priceshare_notes}
                  onChange={handleUpdateField}
                  rows={3}
                  placeholder="Enter details about price shared with customer (e.g. quote value, discounts, terms)"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
                />
              </label>
              <label className="block text-sm font-medium text-slate-600">
                Price inquiry notes
                <textarea
                  name="pi_notes"
                  value={formData.pi_notes}
                  onChange={handleUpdateField}
                  rows={3}
                  placeholder="Enter details about price inquiries (e.g. requested models, budget range, delivery questions)"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
                />
              </label>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-teal-700 px-6 py-2.5 font-semibold text-white hover:bg-teal-800 disabled:opacity-60 transition-colors shadow-sm"
              >
                {saving
                  ? "Saving..."
                  : editingUid
                  ? "Update Customer Details"
                  : "Save Customer Update"}
              </button>
              {editingUid && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg border border-slate-300 px-5 py-2.5 font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  Recent customer updates
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Track and edit customer inquiry notes, price status, and assigned members
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {options.updates.length} records
              </span>
            </div>
            <table className="min-w-full text-left text-sm divide-y divide-slate-200">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Assigned Members</th>
                  <th className="px-4 py-3">Activity</th>
                  <th className="px-4 py-3 min-w-[200px]">Price Share Notes</th>
                  <th className="px-4 py-3 min-w-[200px]">Price Inquiry Notes</th>
                  <th className="px-4 py-3 whitespace-nowrap">Updated</th>
                  <th className="px-4 py-3 text-center whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {options.updates.map((update) => (
                  <tr
                    key={update.uid}
                    className={`transition-colors ${
                      editingUid === update.uid
                        ? "bg-teal-50/60 ring-1 ring-inset ring-teal-400"
                        : "hover:bg-slate-50/70"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{update.customer_name}</div>
                      {update.customer_email && (
                        <div className="text-xs text-slate-500">{update.customer_email}</div>
                      )}
                      {update.customer_mobile && (
                        <div className="text-xs text-slate-400">{update.customer_mobile}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700">
                      {update.product_name || "Catalog product unavailable"}
                    </td>
                    <td className="px-4 py-3">
                      {update.source ? (
                        <span className="inline-block rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 border border-slate-200">
                          {update.source}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {Array.isArray(update.assigned_team_names) && update.assigned_team_names.length ? (
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {update.assigned_team_names.map((name, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-md bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-800 border border-teal-200"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      ) : update.assigned_team_name && update.assigned_team_name !== "Unassigned" ? (
                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                          {update.assigned_team_name.split(",").map((name, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-md bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-800 border border-teal-200"
                            >
                              {name.trim()}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[150px]">
                        {update.priceshare && (
                          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-800 border border-emerald-200">
                            Price shared
                          </span>
                        )}
                        {update.pi && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800 border border-amber-200">
                            Price inquiry
                          </span>
                        )}
                        {update.email && (
                          <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-semibold text-sky-800 border border-sky-200">
                            Email
                          </span>
                        )}
                        {update.whatsapp && (
                          <span className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] font-semibold text-green-800 border border-green-200">
                            WhatsApp
                          </span>
                        )}
                        {!update.priceshare && !update.pi && !update.email && !update.whatsapp && (
                          <span className="text-xs text-slate-400 italic">No activity</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {update.priceshare_notes ? (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-2 text-xs text-emerald-950 font-sans shadow-2xs whitespace-pre-wrap break-words max-w-xs">
                          {update.priceshare_notes}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {update.pi_notes ? (
                        <div className="rounded-lg border border-blue-200 bg-blue-50/80 p-2 text-xs text-blue-950 font-sans shadow-2xs whitespace-pre-wrap break-words max-w-xs">
                          {update.pi_notes}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">
                      {update.updated_date
                        ? new Date(update.updated_date).toLocaleString([], {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : update.created_date
                        ? new Date(update.created_date).toLocaleString([], {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleEditUpdate(update)}
                          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all shadow-xs ${
                            editingUid === update.uid
                              ? "bg-teal-700 text-white ring-2 ring-teal-400 shadow"
                              : "border border-teal-600 bg-teal-50 text-teal-700 hover:bg-teal-600 hover:text-white"
                          }`}
                          title="Edit this customer update"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          {editingUid === update.uid ? "Editing" : "Edit"}
                        </button>
                        {(update.priceshare_notes || update.pi_notes) && (
                          <button
                            type="button"
                            onClick={() => setViewModalUpdate(update)}
                            className="inline-flex items-center rounded-lg border border-slate-300 bg-white p-1.5 text-xs text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                            title="View note details"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!options.updates.length && (
                  <tr>
                    <td className="px-4 py-8 text-center text-slate-500" colSpan={9}>
                      No customer updates have been recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewModalUpdate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Customer Update Details</h3>
                <p className="text-xs text-slate-500">
                  {viewModalUpdate.customer_name} — {viewModalUpdate.product_name}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewModalUpdate(null)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 rounded-lg bg-slate-50 p-3 text-xs">
                <div>
                  <span className="text-slate-500 block">Customer</span>
                  <span className="font-semibold text-slate-800">{viewModalUpdate.customer_name}</span>
                  {viewModalUpdate.customer_email && (
                    <div className="text-slate-500">{viewModalUpdate.customer_email}</div>
                  )}
                  {viewModalUpdate.customer_mobile && (
                    <div className="text-slate-500">{viewModalUpdate.customer_mobile}</div>
                  )}
                </div>
                <div>
                  <span className="text-slate-500 block">Catalog Product</span>
                  <span className="font-semibold text-slate-800">{viewModalUpdate.product_name}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Source</span>
                  <span className="font-semibold text-slate-800">{viewModalUpdate.source || "Not specified"}</span>
                </div>
                <div>
                  <span className="text-slate-500 block">Assigned Member(s)</span>
                  {Array.isArray(viewModalUpdate.assigned_team_names) && viewModalUpdate.assigned_team_names.length ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {viewModalUpdate.assigned_team_names.map((name, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center rounded-md bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-800">
                      {viewModalUpdate.assigned_team_name || "Unassigned"}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Price Share Status & Notes
                </h4>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${viewModalUpdate.priceshare ? "bg-emerald-600" : "bg-slate-300"}`}></span>
                    <span className="font-semibold text-emerald-900 text-xs">
                      {viewModalUpdate.priceshare ? "Price Shared: Yes" : "Price Shared: No"}
                    </span>
                  </div>
                  <div className="text-xs text-emerald-950 whitespace-pre-wrap">
                    {viewModalUpdate.priceshare_notes || <span className="text-slate-400 italic">No price share notes provided.</span>}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Price Inquiry Status & Notes
                </h4>
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full ${viewModalUpdate.pi ? "bg-blue-600" : "bg-slate-300"}`}></span>
                    <span className="font-semibold text-blue-900 text-xs">
                      {viewModalUpdate.pi ? "Price Inquiry: Yes" : "Price Inquiry: No"}
                    </span>
                  </div>
                  <div className="text-xs text-blue-950 whitespace-pre-wrap">
                    {viewModalUpdate.pi_notes || <span className="text-slate-400 italic">No price inquiry notes provided.</span>}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    handleEditUpdate(viewModalUpdate);
                    setViewModalUpdate(null);
                  }}
                  className="rounded-lg bg-teal-700 px-4 py-2 text-xs font-semibold text-white hover:bg-teal-800 transition-colors"
                >
                  Edit This Update
                </button>
                <button
                  type="button"
                  onClick={() => setViewModalUpdate(null)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CustomerManagement;
